/**
 * TestIt Hooks (BL-214).
 *
 * React Query hooks over the reusable test store
 * (/api/heroes/testit/:talentId/*). Powers the modal reuse/history lead-in and
 * the TestIt catalog. Validity is backend-computed — never derive it in a
 * component.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSdkContext } from '../providers/SdkProvider';
import {
  createTestItService,
  type TalentTestResultView,
  type TestitVerdictView,
  type InvitedTestitAssignment,
  type InvitedTest,
} from '../services/testit.service';
import { engagementEvidenceKeys } from './useEngagementEvidence';

export type { TalentTestResultView, TestitVerdictView, InvitedTestitAssignment, InvitedTest };

export const testItKeys = {
  all: ['testit'] as const,
  invitation: () => [...testItKeys.all, 'invitation'] as const,
  results: (talentId: string) => [...testItKeys.all, 'results', talentId] as const,
  // Business reads are per RELATIONSHIP — the talent AND the drawer's journey (X8).
  businessResults: (talentId: string, journeyId: string) =>
    [...testItKeys.all, 'business-results', talentId, journeyId] as const,
  businessOverview: (talentId: string, journeyId: string) =>
    [...testItKeys.all, 'business-overview', talentId, journeyId] as const,
  businessPitchUrl: (talentId: string, journeyId: string) =>
    [...testItKeys.all, 'business-pitch-url', talentId, journeyId] as const,
  businessFailedAttempts: (talentId: string, journeyId: string) =>
    [...testItKeys.all, 'business-failed-attempts', talentId, journeyId] as const,
  businessHistory: (talentId: string, journeyId: string, testId: string, subTest?: string) =>
    [...testItKeys.all, 'business-history', talentId, journeyId, testId, subTest ?? ''] as const,
  timeline: (talentId: string) => [...testItKeys.all, 'timeline', talentId] as const,
  history: (talentId: string, testId: string, subTest?: string) =>
    [...testItKeys.all, 'history', talentId, testId, subTest ?? ''] as const,
};

/** The citizen's invited-TestIt assignment (which specific tests + done status). */
export function useInvitedTestItAssignment(options?: { enabled?: boolean }) {
  const { client } = useSdkContext();
  const service = createTestItService(client);

  return useQuery({
    queryKey: testItKeys.invitation(),
    queryFn: () => service.getInvitation(),
    enabled: options?.enabled ?? true,
  });
}

/** All active (latest) reusable results for a talent. */
export function useTalentTestResults(talentId: string, options?: { enabled?: boolean }) {
  const { client } = useSdkContext();
  const service = createTestItService(client);

  return useQuery({
    queryKey: testItKeys.results(talentId),
    queryFn: () => service.getResults(talentId),
    enabled: (options?.enabled ?? true) && !!talentId,
  });
}

/** Every attempt at every test (newest first) — the TestIt page's History tab. */
export function useTalentTestResultTimeline(talentId: string, options?: { enabled?: boolean }) {
  const { client } = useSdkContext();
  const service = createTestItService(client);

  return useQuery({
    queryKey: testItKeys.timeline(talentId),
    queryFn: () => service.getAllHistory(talentId),
    enabled: (options?.enabled ?? true) && !!talentId,
  });
}

/** History for one test unit (newest first) — powers the modal history select. */
export function useTalentTestResultHistory(
  talentId: string,
  testId: string,
  subTest?: string,
  options?: { enabled?: boolean }
) {
  const { client } = useSdkContext();
  const service = createTestItService(client);

  return useQuery({
    queryKey: testItKeys.history(talentId, testId, subTest),
    queryFn: () => service.getResultHistory(talentId, testId, subTest),
    enabled: (options?.enabled ?? true) && !!talentId && !!testId,
  });
}

/** Standalone submit (P2b): record a self-taken result; invalidates the catalog + history. */
export function useRecordStandaloneTest(talentId: string) {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const service = createTestItService(client);

  return useMutation({
    mutationFn: (dto: {
      testId: string;
      subTest?: string;
      result?: Record<string, unknown> | null;
      score?: string | null;
    }) => service.recordStandalone(talentId, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: testItKeys.results(talentId) });
      // A new attempt supersedes the prior one, so any open modal's history is
      // now stale — invalidate it too (mirrors the delete hook).
      queryClient.invalidateQueries({ queryKey: [...testItKeys.all, 'history', talentId] });
      queryClient.invalidateQueries({ queryKey: testItKeys.timeline(talentId) });
    },
  });
}

/** Soft-delete a result; invalidates the talent's results + that unit's history. */
export function useDeleteTalentTestResult(talentId: string) {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const service = createTestItService(client);

  return useMutation({
    mutationFn: (id: string) => service.deleteResult(talentId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: testItKeys.results(talentId) });
      queryClient.invalidateQueries({ queryKey: [...testItKeys.all, 'history', talentId] });
      queryClient.invalidateQueries({ queryKey: testItKeys.timeline(talentId) });
    },
  });
}

/**
 * Business (Vetting): a candidate's active results, read cross-tenant for the
 * business admin. Pipeline-gated on the backend — use this in business-web, NOT
 * `useTalentTestResults` (the citizen self-read, which returns [] for a business
 * caller).
 */
export function useTalentTestResultsForBusiness(
  talentId: string,
  journeyId: string,
  options?: { enabled?: boolean }
) {
  const { client } = useSdkContext();
  const service = createTestItService(client);

  return useQuery({
    queryKey: testItKeys.businessResults(talentId, journeyId),
    queryFn: () => service.getResultsForBusiness(talentId, journeyId),
    enabled: (options?.enabled ?? true) && !!talentId,
  });
}

/**
 * Business (Vetting): the full TestIt overview — invited tests (with done
 * status) + rich results + completion, in one pipeline-gated call. Powers the
 * Vetting TestIt tab.
 */
export function useTalentTestitOverview(
  talentId: string,
  journeyId: string,
  options?: { enabled?: boolean }
) {
  const { client } = useSdkContext();
  const service = createTestItService(client);

  return useQuery({
    queryKey: testItKeys.businessOverview(talentId, journeyId),
    queryFn: () => service.getOverviewForBusiness(talentId, journeyId),
    enabled: (options?.enabled ?? true) && !!talentId,
  });
}

/**
 * Business (Vetting): language certificates whose latest analysis failed, with
 * the typed reason — so the tab can say "failed" instead of "not taken".
 */
export function useTalentTestitFailedAttempts(
  talentId: string,
  journeyId: string,
  options?: { enabled?: boolean }
) {
  const { client } = useSdkContext();
  const service = createTestItService(client);

  return useQuery({
    queryKey: testItKeys.businessFailedAttempts(talentId, journeyId),
    queryFn: () => service.getFailedAttemptsForBusiness(talentId, journeyId),
    enabled: (options?.enabled ?? true) && !!talentId,
  });
}

/** Business (Vetting): presigned pitch playback URL for the detail sheet. */
export function useTalentTestitPitchUrl(
  talentId: string,
  journeyId: string,
  options?: { enabled?: boolean }
) {
  const { client } = useSdkContext();
  const service = createTestItService(client);

  return useQuery({
    queryKey: testItKeys.businessPitchUrl(talentId, journeyId),
    queryFn: () => service.getPitchUrlForBusiness(talentId, journeyId),
    enabled: (options?.enabled ?? true) && !!talentId,
    // Presigned URLs expire; refetch when the sheet reopens rather than caching long.
    staleTime: 4 * 60 * 1000,
  });
}

/** Business (Vetting): per-test attempt history for the detail sheet. */
export function useTalentTestitResultHistoryForBusiness(
  talentId: string,
  journeyId: string,
  testId: string,
  subTest?: string,
  options?: { enabled?: boolean }
) {
  const { client } = useSdkContext();
  const service = createTestItService(client);

  return useQuery({
    queryKey: testItKeys.businessHistory(talentId, journeyId, testId, subTest),
    queryFn: () => service.getResultHistoryForBusiness(talentId, journeyId, testId, subTest),
    enabled: (options?.enabled ?? true) && !!talentId && !!testId,
  });
}

/** Business (Vetting): nudge the candidate to complete their TestIt (429 if too soon). */
export function useRemindTestit(talentId: string, journeyId: string) {
  const { client } = useSdkContext();
  const service = createTestItService(client);

  return useMutation({
    mutationFn: () => service.remind(talentId, journeyId),
  });
}

/** Business (Vetting): record (or replace) a Pass/Decline verdict for a talent. */
export function useRecordTestitVerdict(talentId: string, journeyId: string) {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const service = createTestItService(client);

  return useMutation({
    mutationFn: (dto: { verdict: 'passed' | 'declined' }) =>
      service.recordVerdict(talentId, { ...dto, journeyId }),
    onSuccess: () => {
      // Every verdict takes an evidence capture (W5.8): the verdict history and
      // the capture list both gained an entry. The history IS the verdict read
      // (D3) — `useVerdictHistory` — so this one invalidation covers both.
      queryClient.invalidateQueries({ queryKey: engagementEvidenceKeys.talent(talentId) });
    },
  });
}
